import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/Add';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpward';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutline';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  createAdminContentItem,
  deleteAdminContentItem,
  fetchAdminContentModule,
  reorderAdminContentItems,
  updateAdminContentItem,
} from '../../services/contentAdminApi';
import { getApiErrorMessage } from '../../services/apiUtils';
import type { AdminContentModuleResponse, HomepageBannerItem } from '../../types/content';
import { fileToPayload } from '../../utils/fileUpload';
import {
  adminActionCardSx,
  adminContentCardSx,
  adminDialogPaperSx,
  adminHeroSx,
  adminMetricIconSx,
  adminSoftChipSx,
  adminSummaryCardSx,
  adminTableCardSx,
} from '../../components/admin/adminVisuals';

function formatDate(value?: string | null) {
  if (!value) return '--';
  return new Date(value).toLocaleString('pt-BR');
}

export default function AdminHomepageBannersPage() {
  const [items, setItems] = useState<HomepageBannerItem[]>([]);
  const [moduleMeta, setModuleMeta] = useState<AdminContentModuleResponse<HomepageBannerItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<HomepageBannerItem | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null
  );
  const [form, setForm] = useState({
    title: '',
    href: '',
    imageUrl: '',
    isActive: true,
    useLink: false,
  });

  const loadItems = useCallback(async (options?: { clearFeedback?: boolean }) => {
    setIsLoading(true);
    if (options?.clearFeedback !== false) {
      setFeedback(null);
    }

    try {
      const response = await fetchAdminContentModule<HomepageBannerItem>('homepageBanners');
      setItems(response.items);
      setModuleMeta(response);
      setHasOrderChanges(false);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível carregar os banners da página inicial.'),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const summary = useMemo(() => {
    return {
      total: items.length,
      active: items.filter((item) => item.isActive).length,
      uploads: items.filter((item) => item.storageType === 'upload').length,
      linked: items.filter((item) => item.storageType !== 'upload').length,
    };
  }, [items]);

  const previewUrl = selectedPreviewUrl || form.imageUrl.trim() || editingItem?.imageUrl || '';

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setSelectedFile(null);
    setForm({
      title: '',
      href: '',
      imageUrl: '',
      isActive: true,
      useLink: false,
    });
  };

  const openCreate = () => {
    setEditingItem(null);
    setSelectedFile(null);
    setForm({
      title: '',
      href: '',
      imageUrl: '',
      isActive: true,
      useLink: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: HomepageBannerItem) => {
    setEditingItem(item);
    setSelectedFile(null);
    setForm({
      title: item.title || '',
      href: item.href || '',
      imageUrl: item.imageUrl || '',
      isActive: item.isActive,
      useLink: Boolean(item.href),
    });
    setDialogOpen(true);
  };

  const move = (index: number, direction: -1 | 1) => {
    setItems((current) => {
      const next = [...current];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return current;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      setHasOrderChanges(true);
      return next.map((item, orderIndex) => ({ ...item, order: orderIndex + 1 }));
    });
  };

  const persistOrder = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      await reorderAdminContentItems<HomepageBannerItem>(
        'homepageBanners',
        items.map((item) => item.id)
      );
      await loadItems({ clearFeedback: false });
      setFeedback({ type: 'success', message: 'Ordem de exibição atualizada com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível salvar a ordem dos banners.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const payload: Record<string, unknown> = {
        title: form.title,
        alt: form.title,
        href: form.useLink ? form.href : '',
        imageUrl: form.imageUrl,
        isActive: form.isActive,
      };

      if (selectedFile) {
        payload.file = await fileToPayload(selectedFile);
      }

      if (editingItem) {
        await updateAdminContentItem<HomepageBannerItem>('homepageBanners', editingItem.id, payload);
        setFeedback({ type: 'success', message: 'Banner atualizado com sucesso.' });
      } else {
        await createAdminContentItem<HomepageBannerItem>('homepageBanners', payload);
        setFeedback({ type: 'success', message: 'Banner criado com sucesso.' });
      }

      resetDialog();
      await loadItems({ clearFeedback: false });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível salvar este banner.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item: HomepageBannerItem) => {
    if (!window.confirm(`Remover o banner "${item.title || item.alt || item.id}"?`)) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await deleteAdminContentItem('homepageBanners', item.id);
      await loadItems({ clearFeedback: false });
      setFeedback({ type: 'success', message: 'Banner removido com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível remover o banner selecionado.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (item: HomepageBannerItem) => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const response = await updateAdminContentItem<HomepageBannerItem>('homepageBanners', item.id, {
        title: item.title,
        alt: item.alt || item.title,
        href: item.href,
        imageUrl: item.imageUrl,
        isActive: !item.isActive,
      });

      await loadItems({ clearFeedback: false });
      setFeedback({
        type: 'success',
        message: response.item.isActive ? 'Banner ativado com sucesso.' : 'Banner desativado com sucesso.',
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível atualizar o status do banner.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled =
    isSubmitting ||
    !form.title.trim() ||
    (!selectedFile && !form.imageUrl.trim()) ||
    (form.useLink && !form.href.trim());

  return (
    <Stack spacing={3}>
      <Box sx={adminHeroSx}>
        <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Destaques da página inicial" variant="outlined" sx={adminSoftChipSx} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
            Banners da página inicial
          </Typography>
          <Typography sx={{ maxWidth: 860, color: 'rgba(22, 50, 79, 0.88)' }}>
            Organize os banners principais da página inicial com imagem, título e destino opcional para navegação.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
            <Chip
              label={hasOrderChanges ? 'Ordem alterada localmente' : 'Ordem sincronizada'}
              color={hasOrderChanges ? 'warning' : 'success'}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)', fontWeight: 700 }}
            />
            <Chip
              label={`Última atualização: ${formatDate(moduleMeta?.updatedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
            <Chip
              label={`Última publicação: ${formatDate(moduleMeta?.lastPublishedAt || moduleMeta?.publishedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
          </Stack>
        </Stack>
      </Box>

      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      {moduleMeta?.lastPublicationStatus && moduleMeta.lastPublicationStatus !== 'published' && (
        <Alert severity="warning">
          {moduleMeta.lastPublicationError ||
            'Não foi possível concluir a última atualização. Revise os dados e tente novamente.'}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(37, 99, 235, 0.12)', '#1d4ed8')}>
                <CollectionsRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.total}
              </Typography>
              <Typography color="text.secondary">Banners cadastrados</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(20, 184, 166, 0.14)', '#0f766e')}>
                <CollectionsRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.active}
              </Typography>
              <Typography color="text.secondary">Banners ativos</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(245, 158, 11, 0.16)', '#b45309')}>
                <CollectionsRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.uploads}
              </Typography>
              <Typography color="text.secondary">Imagens enviadas</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(14, 165, 233, 0.14)', '#0369a1')}>
                <LinkRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.linked}
              </Typography>
              <Typography color="text.secondary">Imagens por endereço</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={adminTableCardSx}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ color: '#16324f' }}>
                Gerenciar banners
              </Typography>
              <Typography color="text.secondary">
                Cadastre, reorganize e mantenha em destaque os banners exibidos na página inicial.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Atualizar">
                <span>
                  <IconButton onClick={() => void loadItems()} disabled={isLoading || isSubmitting}>
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<SaveRoundedIcon />}
                onClick={() => void persistOrder()}
                disabled={!hasOrderChanges || isSubmitting}
                sx={{ borderRadius: 999 }}
              >
                Salvar ordem
              </Button>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ borderRadius: 999 }}>
                Novo banner
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mb: 3 }}>
            Você pode enviar uma imagem do computador ou informar o endereço de uma imagem já publicada.
          </Alert>

          {isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Carregando banners...</Typography>
            </Stack>
          ) : (
            <Grid container spacing={2}>
              {items.map((item, index) => (
                <Grid key={item.id} item xs={12} md={6} xl={4}>
                  <Card sx={{ ...adminActionCardSx, height: '100%', overflow: 'hidden' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        aspectRatio: '16 / 7',
                        bgcolor: '#e6ebf2',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.alt || item.title || `Banner ${index + 1}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, left: 12 }}>
                        <Chip
                          size="small"
                          label={item.isActive ? 'Ativo' : 'Inativo'}
                          color={item.isActive ? 'success' : 'default'}
                          variant={item.isActive ? 'filled' : 'outlined'}
                        />
                        <Chip size="small" label={`Ordem ${item.order}`} variant="outlined" sx={{ bgcolor: '#fff' }} />
                      </Stack>
                    </Box>

                    <CardContent>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                            {item.title || item.alt || 'Banner sem título'}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={item.storageType === 'upload' ? 'Imagem enviada' : 'Imagem por endereço'}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={item.href ? 'Com link' : 'Sem link'}
                            variant="outlined"
                            icon={<LinkRoundedIcon />}
                          />
                        </Stack>

                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Página de destino
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {item.href || 'Nenhuma página vinculada'}
                          </Typography>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Atualizado em {formatDate(item.updatedAt)}
                        </Typography>

                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0 || isSubmitting}>
                            <ArrowUpwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => move(index, 1)}
                            disabled={index === items.length - 1 || isSubmitting}
                          >
                            <ArrowDownwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => void handleToggleActive(item)}
                            disabled={isSubmitting}
                          >
                            {item.isActive ? 'Desativar' : 'Ativar'}
                          </Button>
                          <IconButton size="small" onClick={() => openEdit(item)} disabled={isSubmitting}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleDelete(item)}
                            disabled={isSubmitting}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              {!items.length && (
                <Grid item xs={12}>
                  <Card sx={adminContentCardSx}>
                    <CardContent>
                      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        Nenhum banner cadastrado no momento.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={resetDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: adminDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#16324f' }}>
          {editingItem ? 'Editar banner' : 'Novo banner'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <TextField
                  label="Título do banner"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  fullWidth
                  required
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.useLink}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          useLink: event.target.checked,
                          href: event.target.checked ? current.href : '',
                        }))
                      }
                    />
                  }
                  label="Este banner leva para uma página"
                />

                {form.useLink && (
                  <TextField
                    label="Página ou link"
                    value={form.href}
                    onChange={(event) => setForm((current) => ({ ...current, href: event.target.value }))}
                    fullWidth
                    helperText="Você pode informar uma página do site ou um endereço externo. A abertura acontece na mesma guia."
                  />
                )}

                <TextField
                  label="Endereço da imagem"
                  value={form.imageUrl}
                  onChange={(event) => setForm((current) => ({ ...current, imageUrl: event.target.value }))}
                  fullWidth
                  helperText="Se houver envio de imagem, ele substitui o endereço informado."
                />

                <Button component="label" variant="outlined" sx={{ borderRadius: 999 }}>
                  {selectedFile ? `Imagem selecionada: ${selectedFile.name}` : 'Escolher imagem'}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  />
                </Button>

                <Button
                  variant={form.isActive ? 'contained' : 'outlined'}
                  onClick={() => setForm((current) => ({ ...current, isActive: !current.isActive }))}
                  sx={{ borderRadius: 999 }}
                >
                  {form.isActive ? 'Banner ativo' : 'Banner inativo'}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ ...adminContentCardSx, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 2, color: '#16324f' }}>
                    Prévia do banner
                  </Typography>
                  {previewUrl ? (
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          aspectRatio: '16 / 7',
                          bgcolor: '#e6ebf2',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box
                          component="img"
                          src={previewUrl}
                          alt={form.title || 'Prévia do banner'}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                      <Chip
                        label={selectedFile ? 'Imagem selecionada' : 'Imagem informada'}
                        color="primary"
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      Informe o endereço da imagem ou selecione um arquivo para visualizar o banner antes de salvar.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={resetDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={isSaveDisabled} sx={{ borderRadius: 999 }}>
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
